import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  UserPlusIcon,
  ArrowUturnLeftIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  fetchTool,
  fetchToolCategories,
  fetchToolBrands,
  updateTool,
} from "../../../api/tools";
import DashboardHeader from "../dashboardHeader";
import ToolDialog from "./ToolDialog";
import ToolJournalSection from "./ToolJournalSection";
import ModernEmployeeSelector from "../../utils/Selector/ModernEmployeeSelector";
import { LabelPrintDialog } from "../../utils/labelPrintDialog";

const TOOL_DETAIL_FIELDS =
  "id,title,tool_number,description,status,active,category,brand,supplier_id,supplier,assigned_user_id,assigned_user,internal_notes,model,serial_number,purchase_date,purchase_price,service_interval_days,last_service_date,next_service_date,service_overdue,service_due_soon,document_image,assignment_periods,created_at,updated_at";

const ToolDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tool, setTool] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [showAssignSelector, setShowAssignSelector] = useState(false);
  const [assignSaving, setAssignSaving] = useState(false);
  const [showLabelDialog, setShowLabelDialog] = useState(false);

  const applyToolPayload = useCallback((data) => {
    setTool(data);
    setNotesDraft(data?.internal_notes || "");
  }, []);

  const refreshTool = useCallback(async () => {
    if (!id) return;
    const res = await fetchTool(id, TOOL_DETAIL_FIELDS);
    applyToolPayload(res.data);
  }, [id, applyToolPayload]);

  const loadTool = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      await refreshTool();
    } catch (e) {
      console.error(e);
      toast.error(t("Failed to load tool"));
      navigate("/dashboard/tools");
    } finally {
      setLoading(false);
    }
  }, [id, navigate, refreshTool, t]);

  useEffect(() => {
    loadTool();
  }, [loadTool]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          fetchToolCategories({ per_page: 100 }),
          fetchToolBrands({ per_page: 100 }),
        ]);
        if (!cancelled) {
          setCategories(catRes.data || []);
          setBrands(brandRes.data || []);
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSaveNotes = async () => {
    if (!tool) return;
    setSavingNotes(true);
    try {
      await updateTool(tool.id, { internal_notes: notesDraft });
      await refreshTool();
      toast.success(t("tool.notes_saved"));
    } catch (e) {
      console.error(e);
      toast.error(t("Failed to save"));
    } finally {
      setSavingNotes(false);
    }
  };

  const handleAssignUser = async (selected) => {
    if (!tool || !selected?.length) return;
    setAssignSaving(true);
    try {
      await updateTool(tool.id, {
        assigned_user_id: selected[0].id,
      });
      await refreshTool();
      toast.success(t("tool.assign_saved"));
      setShowAssignSelector(false);
    } catch (e) {
      console.error(e);
      toast.error(t("Failed to save"));
    } finally {
      setAssignSaving(false);
    }
  };

  const handleReturnTool = async () => {
    if (!tool) return;
    setAssignSaving(true);
    try {
      await updateTool(tool.id, { assigned_user_id: null });
      await refreshTool();
      toast.success(t("tool.return_saved"));
    } catch (e) {
      console.error(e);
      toast.error(t("Failed to save"));
    } finally {
      setAssignSaving(false);
    }
  };

  const handleToolUpdated = async () => {
    setShowEditDialog(false);
    try {
      await refreshTool();
    } catch (e) {
      console.error(e);
      toast.error(t("Failed to load tool"));
    }
  };

  if (loading || !tool) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const assignedSelectedEmployees = tool.assigned_user
    ? [
        {
          id: tool.assigned_user.id,
          name:
            tool.assigned_user.name ||
            [tool.assigned_user.first_name, tool.assigned_user.last_name]
              .filter(Boolean)
              .join(" ") ||
            tool.assigned_user.email,
        },
      ]
    : [];

  return (
    <DashboardHeader
      title={tool.title}
      subtitle={tool.tool_number}
      actions={[
        {
          label: t("tool.print_label"),
          icon: PrinterIcon,
          action: () => setShowLabelDialog(true),
          hidden: false,
          menu: false,
        },
        {
          label: t("tool.edit_tool"),
          icon: PencilSquareIcon,
          action: () => setShowEditDialog(true),
          hidden: false,
          menu: false,
        },
      ]}
    >
      <div className="space-y-6 p-4 max-w-5xl">
        <div className="flex flex-wrap items-center gap-3">
          <Link
            to="/dashboard/tools"
            className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            {t("tool.back_to_overview")}
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white rounded-lg shadow border p-4">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">
              {t("tool.status")}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {t(`tool.status.${tool.status}`)}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">
              {t("tool.category")}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {tool.category?.name || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">
              {t("tool.brand")}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {tool.brand?.name || "—"}
            </p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase">
              {t("tool.next_service")}
            </p>
            <p className="mt-1 text-sm text-gray-900">
              {tool.next_service_date
                ? new Date(tool.next_service_date).toLocaleDateString()
                : "—"}
            </p>
          </div>
          {tool.description ? (
            <div className="sm:col-span-2">
              <p className="text-xs font-medium text-gray-500 uppercase">
                {t("tool.description")}
              </p>
              <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">
                {tool.description}
              </p>
            </div>
          ) : null}
        </div>

        <div className="bg-white rounded-lg shadow border p-4 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            {t("tool.assignment_heading")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {t("tool.assignment_hint")}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {tool.assigned_user ? (
              <p className="text-sm text-gray-900">
                <span className="font-medium">{t("tool.assigned_to")}: </span>
                {tool.assigned_user.name ||
                  [
                    tool.assigned_user.first_name,
                    tool.assigned_user.last_name,
                  ]
                    .filter(Boolean)
                    .join(" ") ||
                  tool.assigned_user.email}
              </p>
            ) : (
              <p className="text-sm text-gray-500">{t("tool.not_assigned")}</p>
            )}
            <button
              type="button"
              disabled={assignSaving}
              onClick={() => setShowAssignSelector(true)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              <UserPlusIcon className="h-4 w-4 mr-2" />
              {tool.assigned_user
                ? t("tool.reassign_user")
                : t("tool.assign_user")}
            </button>
            {tool.assigned_user ? (
              <button
                type="button"
                disabled={assignSaving}
                onClick={handleReturnTool}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ArrowUturnLeftIcon className="h-4 w-4 mr-2" />
                {t("tool.return_from_user")}
              </button>
            ) : null}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border overflow-hidden">
          <div className="px-4 py-4 sm:px-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              {t("tool.usage_history_heading")}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {t("tool.usage_history_hint")}
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("tool.usage_user")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("tool.usage_from")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("tool.usage_to")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("tool.usage_days")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("tool.usage_outcome")}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t("tool.usage_status")}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {(tool.assignment_periods || []).length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-sm text-gray-500 text-center"
                    >
                      {t("tool.usage_history_empty")}
                    </td>
                  </tr>
                ) : (
                  (tool.assignment_periods || []).map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {row.user?.name || row.user?.email || "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {row.assigned_on
                          ? new Date(row.assigned_on).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                        {row.returned_on
                          ? new Date(row.returned_on).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.days_held != null ? row.days_held : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {row.ended_reason
                          ? t(`tool.usage_reason.${row.ended_reason}`, {
                              defaultValue: row.ended_reason,
                            })
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            row.status === "active"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {t(`tool.usage_status_${row.status || "ended"}`)}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border p-4 sm:p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            {t("tool.notes_heading")}
          </h2>
          <p className="text-sm text-gray-500 mb-3">{t("tool.notes_hint")}</p>
          <textarea
            className="w-full min-h-[120px] border border-gray-300 rounded-md p-3 text-sm focus:ring-blue-500 focus:border-blue-500"
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder={t("tool.notes_placeholder")}
          />
          <div className="mt-3">
            <button
              type="button"
              disabled={savingNotes}
              onClick={handleSaveNotes}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {savingNotes ? t("Saving") : t("tool.save_notes")}
            </button>
          </div>
        </div>

        <ToolJournalSection tool={tool} />

        <ToolDialog
          open={showEditDialog}
          onClose={() => setShowEditDialog(false)}
          tool={tool}
          categories={categories}
          brands={brands}
          onToolUpdated={handleToolUpdated}
        />

        <LabelPrintDialog
          open={showLabelDialog}
          onClose={() => setShowLabelDialog(false)}
          item={tool}
          labelEntityType="tool"
        />

        {showAssignSelector ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              role="presentation"
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setShowAssignSelector(false)}
              onKeyDown={() => {}}
            />
            <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl z-10">
              <ModernEmployeeSelector
                open={showAssignSelector}
                selectedEmployees={assignedSelectedEmployees}
                onChange={handleAssignUser}
                onClose={() => setShowAssignSelector(false)}
                multiple={false}
                filters={[]}
              />
            </div>
          </div>
        ) : null}
      </div>
    </DashboardHeader>
  );
};

export default ToolDetail;
